import React from 'react';
import { Grid, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Autocomplete, TextField, Typography } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import FormulaField from 'features/common/components/formula-field';
import { FormulaFieldContainer, FormulaFieldLabel } from 'components/common/Styles';

const EngineScoringHeaderComponent = ({
  header,
  isReadOnly,
  dispatch,
  filterTypes,
  targetContexts,
  periodSelectionList,
  validation,
  onChangeTitle,
  partnerTypes,
  partnerBaseTypes
}) => {
  const { t } = useTranslation();
  const onPropertyChange = (property, propertyValue) => {
    if (isReadOnly) {
      if (property !== 'entityIdentifierForTest') return;
    }

    if (property === 'name') onChangeTitle(propertyValue);

    dispatch({ type: 'OnHeaderPropertyChanged', payload: { property, propertyValue } });
  };

  return (
    <>
      <Grid item xs={6} sm={6}>
        <Typography color='error'>{isReadOnly && t('EngineScoring.IsUsedMessage')}</Typography>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name='name'
            value={header.name || ''}
            label={t('EngineScoring.Name')}
            onChange={event => onPropertyChange('name', event)}
            disabled={header.version > 1 || isReadOnly}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            name='code'
            fullWidth
            value={header.code || ''}
            label={t('EngineScoring.Code')}
            onChange={event => onPropertyChange('code', event)}
            disabled={header.version > 1 || isReadOnly}
            error={!isValid(validation.code)}
            helperText={getErrors(validation.code)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField name='version' fullWidth value={header.version || ''} label={t('EngineScoring.Version')} disabled={true} />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={6} xl={2}>
          <DateTime
            name='validFrom'
            value={header.validFrom}
            timeFormat={false}
            label={t('EngineScoring.ValidFrom')}
            onChange={value => onPropertyChange('validFrom', value)}
            disabled={isReadOnly}
            error={!isValid(validation.validFrom)}
            helperText={getErrors(validation.validFrom)}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={2}>
          <DateTime
            name='validTo'
            value={header.validTo}
            timeFormat={false}
            label={t('EngineScoring.ValidTo')}
            onChange={value => onPropertyChange('validTo', value)}
            disabled={isReadOnly}
            error={!isValid(validation.validTo)}
            helperText={getErrors(validation.validTo)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            isNumeric
            fullWidth
            value={header.financialIndicatorDimensionCount}
            disabled={isReadOnly}
            decimalScale={0}
            label={t('EngineScoring.FinancialIndicator')}
            onChange={event => onPropertyChange('financialIndicatorDimensionCount', event)}
          />
        </Grid>
        <Grid item xs={8} sm={2}>
          <TextField
            isNumeric
            fullWidth
            value={header.displayOrder}
            disabled={isReadOnly}
            decimalScale={0}
            label={t('EngineScoring.DisplayOrder')}
            onChange={event => onPropertyChange('displayOrder', event)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            isNumeric
            fullWidth
            decimalScale={0}
            value={header.evaluationOrder}
            disabled={isReadOnly}
            onChange={event => onPropertyChange('evaluationOrder', event)}
            label={t('EngineScoring.EvaluationOrder')}
          />
        </Grid>
        <Grid item xs={12} sm={3} xl={1}>
          <FormControlLabel
            control={<Checkbox checked={header.defaultYN || false} disabled={isReadOnly} />}
            label={t('EngineScoring.DefaultYN')}
            onChange={event => onPropertyChange('defaultYN', event.target.checked)}
            name='defaultYN'
          />
        </Grid>
        <Grid item xs={12} sm={3} xl={1}>
          <FormControlLabel
            control={<Checkbox checked={header.active} disabled={isReadOnly} />}
            label={t('EngineScoring.Active')}
            onChange={event => onPropertyChange('active', event.target.checked)}
            name='active'
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={2}>
          <Autocomplete
            fullWidth
            value={header.targetContextId}
            simpleValue
            options={targetContexts}
            label={t('EngineScoring.Context')}
            onChange={event => onPropertyChange('targetContextId', event)}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={4} xl={3}>
          <TextField
            fullWidth
            value={header.docStructureKey || ''}
            label={t('EngineScoring.DocStructureKey')}
            onChange={event => onPropertyChange('docStructureKey', event)}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <Tooltip title={t('EngineScoring.Tooltip.EntityIdentifierForTest')}>
            <TextField
              fullWidth
              value={header.entityIdentifierForTest || ''}
              label={t('EngineScoring.EntityIdentifierForTest')}
              onChange={event => onPropertyChange('entityIdentifierForTest', event)}
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6} xl={2}>
          <Autocomplete
            fullWidth
            value={header.periodSelectionModeId}
            simpleValue
            options={periodSelectionList}
            label={t('EngineScoring.PeriodModel')}
            onChange={event => onPropertyChange('periodSelectionModeId', event)}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={2}>
          <Autocomplete
            fullWidth
            isClearable={true}
            value={header.filterTypeId}
            simpleValue
            options={filterTypes}
            label={t('EngineScoring.FilterType')}
            onChange={event => onPropertyChange('filterTypeId', event)}
            disabled={isReadOnly}
            error={!isValid(validation.filterTypeId)}
            helperText={getErrors(validation.filterTypeId)}
          />
        </Grid>
        {header.filterTypeId === 4 /* formula */ && (
          <Grid item xs={12} sm={12}>
            <FormulaFieldLabel error={!isValid(validation.partnerTypeFormula)} required>
              {t('EngineScoring.AplicabilityFormula')}
            </FormulaFieldLabel>
            <FormulaFieldContainer>
              <FormulaField value={header.partnerTypeFormula} onChange={value => onPropertyChange('partnerTypeFormula', value)} />
            </FormulaFieldContainer>
          </Grid>
        )}
        {header.filterTypeId === 1 /* partner type */ && (
          <Grid item xs={12} sm={12}>
            <Autocomplete
              isMultiSelection={true}
              isDisabled={isReadOnly}
              simpleValue={true}
              isClearable={true}
              fullWidth
              label={t('EngineScoring.PartnerTypes')}
              isSearchable={true}
              options={partnerTypes}
              onChange={event => onPropertyChange('partnerTypeIds', event)}
              value={header.partnerTypeIds}
              error={!isValid(validation.partnerTypes)}
              helperText={getErrors(validation.partnerTypes)}
            />
          </Grid>
        )}
        {header.filterTypeId === 2 /* partner base type */ && (
          <Grid item xs={12} sm={12}>
            <Autocomplete
              isMultiSelection={true}
              isDisabled={isReadOnly}
              simpleValue={true}
              isClearable={true}
              fullWidth
              label={t('EngineScoring.PartnerBaseTypes')}
              isSearchable={true}
              options={partnerBaseTypes}
              onChange={event => onPropertyChange('partnerBaseTypeIds', event)}
              value={header.partnerBaseTypeIds}
              error={!isValid(validation.partnerBaseTypes)}
              helperText={getErrors(validation.partnerBaseTypes)}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

EngineScoringHeaderComponent.propTypes = {
  header: PropTypes.object,
  isReadOnly: PropTypes.bool,
  loading: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  filterTypes: PropTypes.array,
  targetContexts: PropTypes.array,
  periodSelectionList: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onRedirect: PropTypes.func.isRequired,
  validation: PropTypes.object,
  onChangeTitle: PropTypes.func,
  partnerTypes: PropTypes.array,
  partnerBaseTypes: PropTypes.array
};

export default EngineScoringHeaderComponent;
